import { useState, useEffect, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Ticket } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { usersApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { OnboardingStepProps } from '@/types/components/onboarding'
import { FadeUp } from '../FadeUp'

export const AccessCodeStep = forwardRef<HTMLDivElement, OnboardingStepProps>(
  function AccessCodeStep({ onNext }, ref) {
    const { t } = useTranslation(['onboarding', 'common'])
    const [code, setCode] = useState('')
    const [isApplying, setIsApplying] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
      const pending = sessionStorage.getItem('pending_access_code')
      if (pending) {
        setCode(pending)
        sessionStorage.removeItem('pending_access_code')
      }
    }, [])

    const handleApply = async () => {
      if (!code.trim()) return
      setIsApplying(true)
      setError(null)
      try {
        await usersApi.applyPromoCode(code.trim())
        onNext()
      } catch (err: unknown) {
        setError(getApiErrorMessage(err, t('accessCode.invalid')))
      } finally {
        setIsApplying(false)
      }
    }

    return (
      <div ref={ref} className="flex flex-col items-center gap-8" tabIndex={-1}>
        <FadeUp>
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-primary/10">
              <Ticket className="size-6 text-primary" />
            </div>
            <Heading level={2} size="2xl" className="font-display font-normal tracking-tight">
              {t('accessCode.title')}
            </Heading>
            <Text as="p" size="sm" color="muted" className="max-w-sm">
              {t('accessCode.body')}
            </Text>
          </div>
        </FadeUp>

        <FadeUp delay={100}>
          <div className="flex w-full max-w-xs flex-col gap-4">
            <Input
              data-testid="onboarding-access-code-input"
              type="text"
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(null) }}
              placeholder={t('accessCode.placeholder')}
              autoComplete="off"
              autoFocus
            />

            {error && (
              <Text as="p" size="xs" data-testid="onboarding-access-code-error" className="text-destructive text-center">
                {error}
              </Text>
            )}

            <div className="flex flex-col gap-2">
              <Button
                data-testid="onboarding-access-code-apply"
                variant="cta"
                className="w-full"
                onClick={handleApply}
                disabled={!code.trim() || isApplying}
              >
                {isApplying ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    {t('accessCode.applying')}
                  </>
                ) : (
                  t('accessCode.apply')
                )}
              </Button>
              <Button
                data-testid="onboarding-access-code-skip"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={onNext}
                disabled={isApplying}
              >
                {t('accessCode.skip')}
              </Button>
            </div>
          </div>
        </FadeUp>
      </div>
    )
  },
)
